import cn from 'classnames';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { SortStartHandler } from 'react-sortable-hoc';
import Icon from 'components/Icon';
import { Col, Row } from 'components/layout';
import { SortableItem, SortableList, Trigger } from 'components/Sortable';

import { CompanyRequest } from 'lib/models/company';
import AddNewCard from '../AddNewCard';
import EditableCard from '../EditableCard';
import Gamification, { GamificationProps } from '../Gamification';
import SectionWrapper from '../SectionWrapper';

import styles from './BenefitsDetails.module.scss';

export interface BenefitsDetailsProps extends GamificationProps {
  showProgressBanner: boolean;
  sectionClass?: string;
}

export const BenefitsDetails: React.FC<BenefitsDetailsProps> = memo(
  ({
    showProgressBanner,
    progressDetails,
    profileCompletePercent,
    nonCompletedSection,
    handleProfileCompletionButton,
    handleNextButton,
    showNextButton,
    sectionClass,
  }: BenefitsDetailsProps) => {
    const { control, watch } = useFormContext();

    const { update, append, move } = useFieldArray({
      control,
      name: 'benefit_stats_attributes',
    });

    const [dragItemIndex, setDragItemIndex] = useState<number | null>(null);
    const [hoverItemIndex, setHoverItemIndex] = useState<number | null>(null);
    const [showProgressBannerState, setShowProgressBannerState] =
      React.useState<boolean>(showProgressBanner);

    useEffect(() => {
      if (showProgressBannerState === false)
        setShowProgressBannerState(showProgressBanner);
    }, [showProgressBanner]);

    const benefitStats: CompanyRequest['benefit_stats_attributes'] = watch(
      'benefit_stats_attributes'
    );

    const remove = (index: number) => {
      const updatedValues = benefitStats.filter((item) => !item._destroy)[
        index
      ];
      const indexToUpdate = benefitStats.indexOf(updatedValues);
      updatedValues._destroy = true;
      update(indexToUpdate, updatedValues);
    };

    const onSortStart: SortStartHandler = ({ index }) => {
      setDragItemIndex(index);
    };
    const onSortEnd = ({
      oldIndex,
      newIndex,
    }: {
      oldIndex: number;
      newIndex: number;
    }): void => {
      setDragItemIndex(null);
      move(oldIndex, newIndex);
    };

    const sortableItemElement = useMemo(() => {
      return benefitStats
        .filter((item) => !item._destroy)
        .map((item, index) => {
          const showControls =
            dragItemIndex === null
              ? hoverItemIndex === index
              : dragItemIndex === index;
          return (
            <SortableItem key={item.id || uuidv4()} index={index}>
              <EditableCard
                data={{ ...item }}
                isUrl={false}
                title={'Edit benefit'}
                index={index}
                onSave={update}
                onRemove={remove}
                onMouseEnter={() => setHoverItemIndex(index)}
                onMouseLeave={() => setHoverItemIndex(null)}
                key={index}
                trigger={
                  <Trigger className={styles.Trigger}>
                    <Icon iconName="drag-and-drop" size="xsmallplus" />
                  </Trigger>
                }
                isGrabbing={dragItemIndex === index}
                showControls={showControls}
              />
            </SortableItem>
          );
        });
    }, [benefitStats, dragItemIndex]);

    return (
      <div id={`section_${progressDetails?.id}`} className={sectionClass}>
        {showProgressBannerState && (
          <Gamification
            profileCompletePercent={profileCompletePercent}
            nonCompletedSection={nonCompletedSection}
            progressDetails={progressDetails}
            handleProfileCompletionButton={() => {
              setShowProgressBannerState(false);
              handleProfileCompletionButton();
            }}
            handleNextButton={() => {
              setShowProgressBannerState(false);
              handleNextButton && handleNextButton();
            }}
            showNextButton={showNextButton}
          />
        )}
        <SectionWrapper
          title="Benefits"
          description="Highlight benefits such as healthcare, paid time off, work-life balance and more. Candidates value these perks as it impacts their financial stability, quality of life and job satisfaction."
          helperText="(Optional)"
          className={cn({ [styles.noBorderRadius]: showProgressBannerState })}
        >
          <Row>
            <Col span={24}>
              <SortableList
                lockAxis="y"
                lockToContainerEdges={true}
                useDragHandle={true}
                onSortStart={onSortStart}
                onSortEnd={onSortEnd}
              >
                {sortableItemElement}
              </SortableList>

              <AddNewCard
                isUrl={false}
                title={'Add benefit'}
                onSave={append}
                placeHolder="Flexible Working Hours"
                modalPlaceholders={{
                  title: 'Flexible Working Hours',
                  subTitle:
                    'Choose your own schedule and maintain work-life balance. Work remotely or adjust hours to fit personal commitments.',
                }}
                itemCount={sortableItemElement?.length}
              />
            </Col>
          </Row>
        </SectionWrapper>
      </div>
    );
  }
);

BenefitsDetails.displayName = 'BenefitsDetails';
